import { Inject, Injectable, InjectionToken } from '@angular/core';

import { environment } from 'appEnv/environment';
import { flagsmithConfig, getFlagsmithEnvironmentKey } from 'appEnv/flagsmith.config';

import { FeatureFlagStore, FeatureFlagMap } from '../classes/common/feature-flag.store';
import { FlagsmithClientInterface, FlagsmithFlagsInterface } from '../interfaces/common/flagsmith-client.interface';

export const FLAGSMITH_CLIENT = new InjectionToken<FlagsmithClientInterface>('FLAGSMITH_CLIENT');

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagsService {
    constructor(@Inject(FLAGSMITH_CLIENT) private readonly client: FlagsmithClientInterface) {
    }

    private callSdk(environmentKey: string): Promise<boolean> {
        return Promise.resolve()
            .then(() => this.client.init({
                environmentID: environmentKey,
                identity: environment.application,
                traits: {
                    tenant: environment.tenant,
                    authId: environment.authId,
                    env: environment.env,
                },
                cacheFlags: true
            }))
            .then(() => true)
            .catch((error: any) => {
                console.warn('[FeatureFlags] Flagsmith init failed, using seeded values', error);

                return false;
            });
    }

    private race(promise: Promise<boolean>, timeoutMs: number): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            let settled = false;

            const timer = setTimeout(() => {
                if (!settled) {
                    settled = true;
                    console.warn('[FeatureFlags] Flagsmith did not answer within ' + timeoutMs + 'ms, using seeded values');
                    resolve(false);
                }
            }, timeoutMs);

            promise.then((result: boolean) => {
                if (!settled) {
                    settled = true;
                    clearTimeout(timer);
                    resolve(result);
                }
            });
        });
    }

    private applyRemoteFlags(): boolean {
        let all: FlagsmithFlagsInterface;

        try {
            all = this.client.getAllFlags();
        } catch (error) {
            console.warn('[FeatureFlags] could not read flags from Flagsmith', error);

            return false;
        }

        if (!all) {
            return false;
        }

        const remote: FeatureFlagMap = {};

        Object.keys(all).forEach((flag: string) => {
            remote[flag] = !!(all[flag] && all[flag].enabled);
        });

        FeatureFlagStore.overlay(remote);

        return true;
    }


    private log(environmentKey: string, remoteApplied: boolean): void {
        if (environment.production) {
            return;
        }

        console.log(
            '[FeatureFlags] source=' + (!environmentKey ? 'seed (no key)' : remoteApplied ? 'flagsmith' : 'seed (fallback)'),
            FeatureFlagStore.snapshot()
        );
    }

    async init(): Promise<void> {
        try {
            const environmentKey = getFlagsmithEnvironmentKey();
            let remoteApplied = false;

            if (environmentKey) {
                const reached = await this.race(this.callSdk(environmentKey), flagsmithConfig.initTimeoutMs);

                if (reached) {
                    remoteApplied = this.applyRemoteFlags();
                }
            }

            this.log(environmentKey, remoteApplied);
        } catch (error) {
            console.warn('[FeatureFlags] unexpected failure resolving flags, using seeded values', error);
        }
    }
}
